'use client'

import React, { useEffect, useState, useCallback } from "react";
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetUnitLoadPositionDetailsQuery, useCreateUnitLoadPositionsMutation } from '@/redux/slices/preship/preship-api-slice';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AlertTooltip from '@/components/alert-tooltip';
import { cn } from "@/lib/utils";
import { showSuccessToast, showErrorToast } from "../toast/toast";
import { Button } from "../ui/button";
import UnitLoadPositionConsolidatedView from "./UnitLoadLengthConsolidatedView";
import { UnitLoadPositionService } from "@/services/UnitLoadPositionService";
import EditBanner from "./EditBanner";
import { Info } from "lucide-react";
interface UnitLoadPosition {
  id?: number;
  variant: string;
  sideAPosition: string;
  sideBPosition: string;
  sideCPosition: string;
  sideDPosition: string;
}

interface PalletLpnNumber {
  id?: number;
  lpnNo: string;
  endStatusPallet: string;
  unitLoadPositions: UnitLoadPosition[];
}

interface FormValues {
  palletLpnNumbers: PalletLpnNumber[];
}

interface UnitLoadPositionProps {
  variant: string;
  week: string[];
  palletId?: number;
  onFormChange?: (hasChanges: boolean) => void;
  isEditing?: boolean
  isOffline?: boolean
  setIsLoading: (isLoading: boolean) => void;
}

export default function UnitLoadPosition({ variant, week, palletId, onFormChange, tabName, stage, isOffline, setIsLoading }: UnitLoadPositionProps) {
  const { activePallet } = useSelector((state: RootState) => state.pallets);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isConsolidatedViewOpen, setIsConsolidatedViewOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  const { data: unitLoadData, isLoading, error, refetch } = useGetUnitLoadPositionDetailsQuery({ palletId: activePallet?.id, variant }, {
    skip: !activePallet,
    refetchOnMountOrArgChange: true
  });
  console.log(unitLoadData, 'unitLoadData')
  const [isEditing, setIsEditing] = useState(false);
  const [createUnitLoadPositions] = useCreateUnitLoadPositionsMutation();
  const [offlineUnitLoadData, setOfflineUnitLoadData] = useState<any>(null);
  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const res = await UnitLoadPositionService.getUnitLoadPositionDetails(Number(palletId), variant)
          setOfflineUnitLoadData(res)
        } catch (e) {
          console.log('error in getData')
          console.log(e)
        }
      }
      getData()
    }
  }, [isOffline, activePallet])
  useEffect(() => {
    return () => {
      if (isEditing) {
        setIsEditing(false)
      }
    }
  }, [isEditing])
  console.log(offlineUnitLoadData, 'offlineUnitLoadData')
  const formik = useFormik<FormValues>({
    initialValues: {
      palletLpnNumbers: [],
    },
    onSubmit: async (values) => {
      try {
        if (isOffline) {
          await UnitLoadPositionService.createUnitLoadPositions({
            palletId: Number(palletId),
            palletLpnNumbers: values.palletLpnNumbers,
          })
          showSuccessToast("Successfully saved unit load positions!");
          return;
        } else {
          const payload = {
            palletId: activePallet?.id || 0,
            palletLpnNumbers: values.palletLpnNumbers.map(pln => ({
              ...pln,
              unitLoadPositions: pln.unitLoadPositions.map(ulp => ({
                ...ulp,
                variant: variant,
              })),
            })),
          };
          await createUnitLoadPositions(payload).unwrap();
          console.log(payload, 'createunitload')
          showSuccessToast("Successfully saved unit load positions!");
        }
      } catch (error) {
        showErrorToast("Failed to save unit load positions");
        console.error('Failed to save unit load positions:', error);
      }
    },
  });

  useEffect(() => {
    if (activePallet && !isLoading && !isOffline) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (error && 'status' in error && error.status === 404) {
        initialPalletLpnNumbers = Array.from({ length: Number(activePallet.numberOfPallets) }, (_, index): PalletLpnNumber => ({
          lpnNo: '',
          endStatusPallet: '',
          unitLoadPositions: [{
            variant: variant,
            sideAPosition: '',
            sideBPosition: '',
            sideCPosition: '',
            sideDPosition: '',
          }],
        }));
      } else if (unitLoadData && unitLoadData.palletLpnNumbers && unitLoadData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = unitLoadData.palletLpnNumbers.map((pln: any) => ({
          ...pln,
          endStatusPallet: pln.endStatusPallet || '',
          unitLoadPositions: pln.unitLoadPositions.length > 0 ? pln.unitLoadPositions : [{
            variant: variant,
            sideAPosition: '',
            sideBPosition: '',
            sideCPosition: '',
            sideDPosition: '',
          }],
        }));
      }

      while (initialPalletLpnNumbers.length < Number(activePallet.numberOfPallets)) {
        initialPalletLpnNumbers.push({
          lpnNo: '',
          endStatusPallet: '',
          unitLoadPositions: [{
            variant: variant,
            sideAPosition: '',
            sideBPosition: '',
            sideCPosition: '',
            sideDPosition: '',
          }],
        });
      }

      const newValues = { palletLpnNumbers: initialPalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [unitLoadData, activePallet, error, variant, activePallet?.numberOfPallets, isLoading]);
  useEffect(() => {
    if (isOffline && offlineUnitLoadData) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (offlineUnitLoadData && offlineUnitLoadData.palletLpnNumbers && offlineUnitLoadData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = offlineUnitLoadData.palletLpnNumbers.map((pln: any) => ({
          ...pln,
          endStatusPallet: pln.endStatusPallet || '',
          unitLoadPositions: pln.unitLoadPositions.length > 0 ? pln.unitLoadPositions : [{
            variant: variant,
            sideAPosition: '',
            sideBPosition: '',
            sideCPosition: '',
            sideDPosition: '',
          }],
        }));
      }

      while (initialPalletLpnNumbers.length < Number(activePallet?.numberOfPallets)) {
        initialPalletLpnNumbers.push({
          lpnNo: '',
          endStatusPallet: '',
          unitLoadPositions: [{
            variant: variant,
            sideAPosition: '',
            sideBPosition: '',
            sideCPosition: '',
            sideDPosition: '',
          }],
        });
      }

      const newValues = { palletLpnNumbers: initialPalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [isOffline, offlineUnitLoadData, activePallet]);

  useEffect(() => {
    if (onFormChange && initialValues) {
      const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues);
      onFormChange(hasChanges);
    }
  }, [formik.values, onFormChange, initialValues]);
  useEffect(() => {
    setIsLoading(formik.isSubmitting);
  }, [formik.isSubmitting, setIsLoading])

  // Add roundToNearestQuarter function
  const roundToNearestQuarter = (value: string): string => {
    if (!value || isNaN(Number(value))) return value;
    const num = parseFloat(value);
    const rounded = Math.round(num * 4) / 4;
    return rounded.toFixed(2);
  };

  // Add keyboard navigation handler to move focus with arrow keys
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, rowIndex: number, colIndex: number) => {
    let targetRow = rowIndex;
    let targetCol = colIndex;
    switch (e.key) {
      case 'ArrowUp':
        targetRow = Math.max(0, rowIndex - 1);
        break;
      case 'Enter': // Adding Enter key to behave like Down arrow - Excel behavior
      case 'ArrowDown':
        targetRow = Math.min(formik.values.palletLpnNumbers.length - 1, rowIndex + 1);
        break;
      case 'ArrowLeft':
        targetCol = Math.max(1, colIndex - 1);
        break;
      case 'ArrowRight':
        targetCol = Math.min(5, colIndex + 1);
        break;
      default:
        return;
    }
    e.preventDefault();
    const nextEl = document.querySelector(`[data-row=\"${targetRow}\"][data-col=\"${targetCol}\"]`) as HTMLElement | null;
    nextEl?.focus();
  }, [formik.values.palletLpnNumbers.length]);

  // if (isLoading) {
  //   return <div>Loading...</div>;
  // }

  const endStatusOptions = [
    "Pre Ship",
    "Post Ship",
    "Stack start(stacked+30m)",
    "Stack Week-1",
    "Stack Week-2",
    "Stack Week-3",
    "Stack Week-4 (unstacking+30M)"
  ];

  return (
    <div className=" space-y-4">
      {!isOffline && <EditBanner

        stage={stage}
        palletId={palletId}
        component={tabName}
        onEditStateChange={setIsEditing}
      />}
      <form id={`unit-load-position-table-${activePallet?.id}`} onSubmit={formik.handleSubmit}>

        <Table className={`${(isEditing) || (isOffline) ? " " : "pointer-events-none"} border-collapse`}>
          <TableHeader>
            <TableRow>
              <TableHead className="border border-gray-300 text-blue p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left" colSpan={3}>
                Pallet Name: {activePallet?.name || 'N/A'} - Mode {activePallet?.shippingModes?.map((sp) => sp.mode).join(', ') || 'N/A'}
              </TableHead>
              <TableHead className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-center" colSpan={4}>
           {!isOffline &&     <div className='flex !pointer-events-auto items-center gap-2 justify-between'>
                <span className="text-xs flex items-center gap-2 text-blue"><Info className="w-4 h-4" /> Note: Positive measurement is overhang and a negative one is underhang</span>
                  <div className="flex-center gap-4">
                    {/* <Button type='button' className="bg-white text-md font-semibold text-blue border-2 border-blue">
                    Clear All
                  </Button> */}
                    <Button
                      onClick={() => setIsConsolidatedViewOpen(true)}
                      type='button' className="bg-white hover:bg-blue hover:text-white text-md font-semibold text-blue border-2 border-blue">
                      Consolidated View
                    </Button>
                  </div>
                </div>}
              </TableHead>
            </TableRow>
            <TableRow>
              {["Pallet No", "Pallet Identifier", "End Status", "Side A Position (inch)", "Side B Position (inch)", "Side C Position (inch)", "Side D Position (inch)"].map((head) => (
                <TableHead
                  key={head}
                  className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left"
                >
                  {head}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {formik.values.palletLpnNumbers.map((pln, index) => {
              const isRowEditable = week.includes(pln.endStatusPallet);
              return (
                <TableRow key={index}>
                  <TableCell className="border border-gray-300 bg-states-disabled p-0">
                    <Input
                      className="w-full h-9 border-none"
                      value={index + 1}
                      readOnly
                    />
                  </TableCell>
                  <TableCell className="border border-gray-300 bg-states-disabled p-0">
                    <div className="relative">
                      <Input
                        className="w-full border-none "
                        {...formik.getFieldProps(`palletLpnNumbers.${index}.lpnNo`)}
                        readOnly={!isRowEditable}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="border border-gray-300 p-0">
                    <div className="relative">
                      <Select
                        value={pln.endStatusPallet}
                        onValueChange={(value) => {
                          if (value !== '') {
                            formik.setFieldValue(`palletLpnNumbers.${index}.endStatusPallet`, value)
                          }
                        }}
                      >
                        <SelectTrigger
                          data-row={index} data-col={1}
                          onKeyDown={(e) => handleKeyDown(e, index, 1)}
                          className="w-full rounded-none bg-transparent border-2 border-required focus:border-blue"
                        >
                          <SelectValue placeholder="Select End Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {endStatusOptions.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  {['sideAPosition', 'sideBPosition', 'sideCPosition', 'sideDPosition'].map((field, colIndex) => (
                    <TableCell key={field} className="border border-gray-300 p-0">
                      <div className="relative">
                        <Input
                          data-row={index} data-col={2 + colIndex}
                          onKeyDown={(e) => handleKeyDown(e, index, 2 + colIndex)}
                          className={cn("w-full h-9 border-none", !isRowEditable && "bg-gray-200")}
                          type="number"
                          step="0.25"
                          placeholder="Enter"
                          value={formik.values.palletLpnNumbers[index].unitLoadPositions[0][field]}
                          onChange={(e) => {
                            // Allow free typing without rounding
                            formik.setFieldValue(`palletLpnNumbers.${index}.unitLoadPositions.0.${field}`, e.target.value);
                          }}
                          onBlur={(e) => {
                            if (e.target.value) {
                              // Only round when user finishes typing (on blur)
                              const roundedValue = roundToNearestQuarter(e.target.value);
                              formik.setFieldValue(`palletLpnNumbers.${index}.unitLoadPositions.0.${field}`, roundedValue);
                            }
                          }}
                          readOnly={!isRowEditable}
                          disabled={!isRowEditable}
                        />
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <button type="submit" style={{ display: 'none' }}></button>
        <UnitLoadPositionConsolidatedView
          isOpen={isConsolidatedViewOpen}
          onClose={() => setIsConsolidatedViewOpen(false)}
          variant={variant}
        />
      </form>
    </div>
  );
}
